import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  putStreamBody,
  useLazyChatStreamQuery,
} from "../store/streamingApi.ts";
const MarketplaceChatContext = createContext(null);
const MARKETPLACE_CONVERSATION_STORAGE_KEY = "marketplace_chat_conversation_id";
const buildApiUrl = (path) => {
  const baseUrl = (
    process.env.REACT_APP_API_BASE_URL || "https://admin.eveness.ai"
  ).replace(/\/$/, "");

  if (baseUrl.endsWith("/api")) return `${baseUrl}${path}`;
  return `${baseUrl}/api${path}`;
};

const normalizeFindProductForRoute = (product) => {
  const normalizedSlug =
    product?.real_slug ||
    product?.slug ||
    product?.product_url ||
    product?.url ||
    "";

  const normalizedProductType = normalizedSlug
    ? String(normalizedSlug).startsWith("mrk-")
      ? "marketplace"
      : "product"
    : "marketplace";

  return {
    id: Number(product?.id),
    name: product?.name || product?.product_name || "Product",
    brand: product?.brand || "",
    price: product?.price || "",
    image: product?.image || product?.image_url || "",
    image_url: product?.image_url || product?.image || "",
    url: product?.url || product?.product_url || "",
    slug: normalizedSlug,
    real_slug: product?.real_slug || normalizedSlug,
    garmentType: product?.garment_type || product?.garmentType || "",
    matchPercentage:
      product?.match_percentage ??
      product?.matchPercentage ??
      product?.product_score ??
      null,
    productType: normalizedProductType,
    product_type: normalizedProductType,
    is_marketplace: normalizedProductType === "marketplace",
  };
};

const getAuthOwnerKey = () => {
  if (typeof window === "undefined") return "guest";
  return window.localStorage.getItem("authToken") || "guest";
};

export const MarketplaceChatProvider = ({ children }) => {
  const navigate = useNavigate();

  const [chatMode, setChatMode] = useState("find");
  const [chatInput, setChatInput] = useState("");
  const [chatImage, setChatImage] = useState(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [marketplaceMessages, setMarketplaceMessages] = useState([]);
  const [activeChatPurpose, setActiveChatPurpose] = useState("chat");
  const [activeChatRequestKey, setActiveChatRequestKey] = useState("");
  const [isModalNewChatActive, setIsModalNewChatActive] = useState(false);

  const [findPreviewOpen, setFindPreviewOpen] = useState(false);
  const [findPreviewQuery, setFindPreviewQuery] = useState("");
  const [findPreviewImage, setFindPreviewImage] = useState(null);
  const [findPreviewProducts, setFindPreviewProducts] = useState([]);
  const [findPreviewMessage, setFindPreviewMessage] = useState("");
  const [isFindPreviewLoading, setIsFindPreviewLoading] = useState(false);

  const [marketplaceConversationId, setMarketplaceConversationId] =
    useState(null);
  const marketplaceConversationIdRef = useRef(null);
  const [isProductTransferLoading, setIsProductTransferLoading] =
    useState(false);
  const [productTransferDraft, setProductTransferDraft] = useState(null);

  const completedChatRequestRef = useRef("");
  const authOwnerKeyRef = useRef(getAuthOwnerKey());

  const [
    triggerChatStream,
    { data: chatStreamData, isFetching: isChatStreamFetching },
  ] = useLazyChatStreamQuery();

  const activeChatStreamData =
    chatStreamData?.requestKey === activeChatRequestKey ? chatStreamData : null;
  const isStreaming = Boolean(activeChatRequestKey) && isChatStreamFetching;

  const updateMarketplaceConversationId = useCallback((conversationId) => {
    const normalizedConversationId =
      conversationId === null ||
      conversationId === undefined ||
      conversationId === ""
        ? null
        : String(conversationId);

    marketplaceConversationIdRef.current = normalizedConversationId;
    setMarketplaceConversationId(normalizedConversationId);

    if (typeof window === "undefined") return;

    if (normalizedConversationId) {
      window.localStorage.setItem(
        MARKETPLACE_CONVERSATION_STORAGE_KEY,
        normalizedConversationId,
      );
    } else {
      window.localStorage.removeItem(MARKETPLACE_CONVERSATION_STORAGE_KEY);
    }
  }, []);

  const invalidateActiveRequest = useCallback(() => {
    completedChatRequestRef.current = "";
    setActiveChatRequestKey("");
    setActiveChatPurpose("chat");
    setIsProductTransferLoading(false);
    setProductTransferDraft(null);
  }, []);

  const resetFindPreview = useCallback(() => {
    setFindPreviewOpen(false);
    setFindPreviewProducts([]);
    setFindPreviewMessage("");
    setFindPreviewQuery("");
    setFindPreviewImage(null);
    setIsFindPreviewLoading(false);
  }, []);

  const clearChatSession = useCallback(
    ({ closeModal = true } = {}) => {
      invalidateActiveRequest();
      updateMarketplaceConversationId(null);
      setMarketplaceMessages([]);
      setChatInput("");
      setChatImage(null);
      setIsModalNewChatActive(false);
      resetFindPreview();

      if (closeModal) setIsChatModalOpen(false);
    },
    [
      invalidateActiveRequest,
      resetFindPreview,
      updateMarketplaceConversationId,
    ],
  );

  useEffect(() => {
    const handleAuthChanged = () => {
      const nextOwnerKey = getAuthOwnerKey();

      if (nextOwnerKey === authOwnerKeyRef.current) return;

      authOwnerKeyRef.current = nextOwnerKey;
      clearChatSession({ closeModal: true });
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
    };
  }, [clearChatSession]);

  const handleMarketplaceConversationChange = useCallback(
    (conversationId) => {
      invalidateActiveRequest();
      updateMarketplaceConversationId(conversationId);
      setIsModalNewChatActive(false);
    },
    [invalidateActiveRequest, updateMarketplaceConversationId],
  );

  const handleMessagesLoaded = useCallback((messages) => {
    setMarketplaceMessages(Array.isArray(messages) ? messages : []);
  }, []);

  const openChatModal = useCallback(() => {
    setIsChatModalOpen(true);
  }, []);

  const closeChatModal = useCallback(() => {
    setIsChatModalOpen(false);
    setIsModalNewChatActive(false);
  }, []);

  const toggleChatModal = useCallback(() => {
    setIsChatModalOpen((current) => !current);
  }, []);

  const startNewChat = useCallback(() => {
    invalidateActiveRequest();
    updateMarketplaceConversationId(null);
    setMarketplaceMessages([]);
    setChatImage(null);
    resetFindPreview();
    setIsModalNewChatActive(true);
    setIsChatModalOpen(true);
  }, [
    invalidateActiveRequest,
    resetFindPreview,
    updateMarketplaceConversationId,
  ]);

  const handleDeletedConversation = useCallback(() => {
    invalidateActiveRequest();
    updateMarketplaceConversationId(null);
    setMarketplaceMessages([]);
    setChatImage(null);
    resetFindPreview();
    setIsModalNewChatActive(true);
    setIsChatModalOpen(true);
  }, [
    invalidateActiveRequest,
    resetFindPreview,
    updateMarketplaceConversationId,
  ]);

  const submitMessage = useCallback(
    ({
      canUseRealChat = false,
      isProfileChecking = false,
      onRequireProfile,
      textOverride,
      image,
      startNewConversation = false,
      forceOpenChat = false,
      productContext = null,
    } = {}) => {
      const text = String(textOverride ?? chatInput).trim();
      const submittedImage =
        image ?? (textOverride === undefined ? chatImage : null);

      if (isProfileChecking) {
        toast.loading("Checking your profile...");
        return false;
      }

      if (!canUseRealChat) {
        closeChatModal();
        onRequireProfile?.();
        return false;
      }

      if (!text || isStreaming) return false;

      const visibleUserMessage = text || "Image attached";
      const userMessage = {
        role: "user",
        text: visibleUserMessage,
        imageFile: submittedImage || null,
        products: [],
      };

      const activeConversationId = marketplaceConversationIdRef.current;
      const includeProductContext = Boolean(productContext);

      const shouldContinueOpenConversation =
        isChatModalOpen && Boolean(activeConversationId);

      const shouldKeepModalChat =
        isChatModalOpen &&
        (isModalNewChatActive || shouldContinueOpenConversation);

      const isFindPreviewRequest =
        chatMode === "find" &&
        !isChatModalOpen &&
        !includeProductContext &&
        !forceOpenChat &&
        !shouldKeepModalChat;

      const shouldStartFreshVisibleChat =
        ((chatMode === "style" && !isChatModalOpen) || isModalNewChatActive) &&
        !includeProductContext;

      if (isFindPreviewRequest) {
        setIsChatModalOpen(false);
        setFindPreviewOpen(false);
        setFindPreviewQuery(visibleUserMessage);
        setFindPreviewImage(submittedImage || null);
        setFindPreviewProducts([]);
        setFindPreviewMessage("");
        setIsFindPreviewLoading(true);
        setActiveChatPurpose("find-preview");
      } else {
        setIsChatModalOpen(true);
        setFindPreviewOpen(false);
        setIsFindPreviewLoading(false);
        setActiveChatPurpose("chat");
      }

      const requestKey = `marketplace-chat-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;

      const formData = new FormData();
      formData.append("text", text);

      if (submittedImage) {
        formData.append("image", submittedImage);
      }

      const shouldSendConversationId =
        !startNewConversation &&
        !shouldStartFreshVisibleChat &&
        !isFindPreviewRequest &&
        Boolean(activeConversationId);

      if (shouldSendConversationId) {
        formData.append("conversation_id", String(activeConversationId));
      }

      if (includeProductContext) {
        const productId = productContext?.productId;
        const productType = productContext?.productType;
        const imageUrl = productContext?.imageUrl;

        if (!productId) {
          toast.error("Product details are not available yet.");
          return false;
        }

        if (productType === "product") {
          formData.append("product_id", String(productId));
        } else {
          formData.append("marketplace_product_id", String(productId));
        }

        if (imageUrl) {
          formData.append("image_url", imageUrl);
        }
      }

      putStreamBody(requestKey, formData);

      completedChatRequestRef.current = "";
      setActiveChatRequestKey(requestKey);

      if (!isFindPreviewRequest) {
        if (startNewConversation || shouldStartFreshVisibleChat) {
          updateMarketplaceConversationId(null);
          setMarketplaceMessages([userMessage]);
          setIsModalNewChatActive(false);
        } else {
          setMarketplaceMessages((current) => [...current, userMessage]);
        }
      }

      if (textOverride === undefined) {
        setChatInput("");
        setChatImage(null);
      }

      triggerChatStream(
        {
          key: requestKey,
          url: buildApiUrl("/new-marketplace-chatbot-streamed/"),
        },
        true,
      );

      return true;
    },
    [
      chatImage,
      chatInput,
      chatMode,
      closeChatModal,
      isChatModalOpen,
      isModalNewChatActive,
      isStreaming,
      triggerChatStream,
      updateMarketplaceConversationId,
    ],
  );

  const startProductConversation = useCallback(
    ({ text, imageUrl, response }) => {
      const normalizedText = String(text || "").trim();
      const normalizedImageUrl = String(imageUrl || "").trim();
      const normalizedResponse = String(response || "").trim();

      if (!normalizedText || !normalizedImageUrl || !normalizedResponse) {
        return false;
      }

      if (isStreaming || isProductTransferLoading) return false;

      const requestKey = `match-transfer-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;

      const formData = new FormData();
      formData.append("text", normalizedText);
      formData.append("image_url", normalizedImageUrl);
      formData.append("response", normalizedResponse);
      formData.append("is_transfer", "true");
      formData.append("web_search", "false");

      invalidateActiveRequest();
      updateMarketplaceConversationId(null);
      resetFindPreview();
      setMarketplaceMessages([]);
      setIsChatModalOpen(false);
      setIsModalNewChatActive(false);
      setProductTransferDraft({
        text: normalizedText,
        imageUrl: normalizedImageUrl,
        fallbackAssistantText: normalizedResponse,
      });
      setActiveChatPurpose("match-transfer");
      setIsProductTransferLoading(true);

      completedChatRequestRef.current = "";
      setActiveChatRequestKey(requestKey);
      putStreamBody(requestKey, formData);

      triggerChatStream(
        {
          key: requestKey,
          url: buildApiUrl("/new-marketplace-chatbot-streamed/"),
        },
        true,
      );

      return true;
    },
    [
      invalidateActiveRequest,
      isProductTransferLoading,
      isStreaming,
      resetFindPreview,
      triggerChatStream,
      updateMarketplaceConversationId,
    ],
  );

  useEffect(() => {
    if (!chatStreamData) return;
    if (!activeChatRequestKey) return;
    if (chatStreamData.requestKey !== activeChatRequestKey) return;

    if (chatStreamData.conversationId) {
      updateMarketplaceConversationId(chatStreamData.conversationId);
    }

    if (!chatStreamData.done) return;
    if (completedChatRequestRef.current === activeChatRequestKey) return;

    completedChatRequestRef.current = activeChatRequestKey;
    setIsFindPreviewLoading(false);

    if (chatStreamData.error) {
      if (activeChatPurpose === "match-transfer") {
        setIsProductTransferLoading(false);
        toast.error(
          chatStreamData.error || "Unable to start the product conversation.",
        );
        return;
      }

      if (activeChatPurpose === "find-preview") {
        setIsChatModalOpen(true);
        setMarketplaceMessages([
          {
            role: "user",
            text: findPreviewQuery || chatInput,
            imageFile: findPreviewImage,
            products: [],
          },
          {
            role: "assistant",
            text: chatStreamData.error || "Something went wrong.",
            products: [],
            isError: true,
          },
        ]);
        return;
      }

      setMarketplaceMessages((current) => [
        ...current,
        {
          role: "assistant",
          text: chatStreamData.error || "Something went wrong.",
          products: [],
          isError: true,
        },
      ]);
      return;
    }

    const returnedProducts = Array.isArray(chatStreamData.products)
      ? chatStreamData.products
      : [];

    if (activeChatPurpose === "match-transfer") {
      setIsProductTransferLoading(false);

      if (!chatStreamData.conversationId) {
        toast.error(
          "The conversation was created without a conversation ID. Please try again.",
        );
        return;
      }

      const assistantMessage =
        String(chatStreamData.text || "").trim() ||
        String(productTransferDraft?.fallbackAssistantText || "").trim();

      updateMarketplaceConversationId(chatStreamData.conversationId);
      setMarketplaceMessages([
        {
          role: "user",
          text: productTransferDraft?.text || "Product analysis",
          imageUrl: productTransferDraft?.imageUrl || "",
          products: [],
        },
        {
          role: "assistant",
          text: assistantMessage,
          products: returnedProducts,
          metadata: chatStreamData.metadata,
        },
      ]);
      setIsModalNewChatActive(false);
      setIsChatModalOpen(true);
      setProductTransferDraft(null);
      return;
    }

    if (activeChatPurpose === "find-preview") {
      setFindPreviewProducts(returnedProducts);
      setFindPreviewMessage(chatStreamData.text || "");
      setFindPreviewOpen(returnedProducts.length > 0);

      if (!returnedProducts.length) {
        setIsChatModalOpen(true);
        setMarketplaceMessages([
          {
            role: "user",
            text: findPreviewQuery || chatInput,
            imageFile: findPreviewImage,
            products: [],
          },
          {
            role: "assistant",
            text:
              chatStreamData.text ||
              "I could not find matching products for this search.",
            products: [],
          },
        ]);
      }

      return;
    }

    setMarketplaceMessages((current) => [
      ...current,
      {
        role: "assistant",
        text: chatStreamData.text || "",
        products: returnedProducts,
        metadata: chatStreamData.metadata,
      },
    ]);
  }, [
    activeChatPurpose,
    activeChatRequestKey,
    chatInput,
    chatStreamData,
    findPreviewImage,
    findPreviewQuery,
    productTransferDraft,
    updateMarketplaceConversationId,
  ]);

  const viewFindProducts = useCallback(() => {
    const query = findPreviewQuery;
    const products = findPreviewProducts.map(normalizeFindProductForRoute);
    resetFindPreview();
    navigate("/marketplace-search-products", {
      state: {
        query,
        products,
      },
    });
  }, [findPreviewProducts, findPreviewQuery, navigate, resetFindPreview]);

  const viewFindGpt = useCallback(() => {
    setFindPreviewOpen(false);
    setIsChatModalOpen(true);
    setActiveChatPurpose("chat");
    setIsModalNewChatActive(false);

    setMarketplaceMessages([
      {
        role: "user",
        text: findPreviewQuery,
        imageFile: findPreviewImage,
        products: [],
      },
      {
        role: "assistant",
        text: findPreviewMessage || "",
        products: findPreviewProducts,
      },
    ]);
  }, [
    findPreviewImage,
    findPreviewMessage,
    findPreviewProducts,
    findPreviewQuery,
  ]);

  const value = useMemo(
    () => ({
      chatMode,
      setChatMode,
      chatInput,
      setChatInput,
      chatImage,
      setChatImage,
      isChatModalOpen,
      setIsChatModalOpen,
      marketplaceMessages,
      setMarketplaceMessages,
      activeChatStreamData,
      isStreaming,
      isFindPreviewLoading,
      findPreviewOpen,
      setFindPreviewOpen,
      findPreviewQuery,
      findPreviewProducts,
      marketplaceConversationId,
      isProductTransferLoading,
      submitMessage,
      startProductConversation,
      openChatModal,
      closeChatModal,
      toggleChatModal,
      startNewChat,
      handleDeletedConversation,
      handleMarketplaceConversationChange,
      handleMessagesLoaded,
      viewFindProducts,
      viewFindGpt,
      resetFindPreview,
      clearChatSession,
    }),
    [
      activeChatStreamData,
      chatImage,
      chatInput,
      chatMode,
      clearChatSession,
      closeChatModal,
      findPreviewOpen,
      findPreviewProducts,
      findPreviewQuery,
      handleDeletedConversation,
      handleMarketplaceConversationChange,
      handleMessagesLoaded,
      isChatModalOpen,
      isFindPreviewLoading,
      isProductTransferLoading,
      isStreaming,
      marketplaceConversationId,
      marketplaceMessages,
      openChatModal,
      resetFindPreview,
      startNewChat,
      startProductConversation,
      submitMessage,
      toggleChatModal,
      viewFindGpt,
      viewFindProducts,
    ],
  );

  return (
    <MarketplaceChatContext.Provider value={value}>
      {children}
    </MarketplaceChatContext.Provider>
  );
};

export const useMarketplaceChat = () => {
  const context = useContext(MarketplaceChatContext);

  if (!context) {
    throw new Error(
      "useMarketplaceChat must be used inside MarketplaceChatProvider.",
    );
  }

  return context;
};
